import React, { useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { AppleSignIn, SignInScope } from '@capawesome/capacitor-apple-sign-in';
import { toast } from 'sonner';

/**
 * AppleAuthButton Component
 * @param {Object} props
 * @param {Function} props.onSuccess - Callback when login succeeds with credential token
 * @param {Function} props.onError - Callback when login fails
 * @param {string} props.text - Button text ('signin_with', 'signup_with', 'continue_with')
 */
export const AppleAuthButton = ({ onSuccess, onError, text = 'signin_with' }) => {
  const platform = Capacitor.getPlatform();
  const isAuthenticatingRef = useRef(false);
  
  // Apple Sign In is mandatory for iOS if other social logins are present.
  // We also support it on Web and Android via the Service ID configuration.

  const handleSuccess = (credential, fullName = null) => {
    isAuthenticatingRef.current = false;
    if (onSuccess) {
      onSuccess(credential, fullName);
    }
  };

  const handleError = (error) => {
    isAuthenticatingRef.current = false;
    console.error('Apple Sign-In Error:', error);
    // Don't show toast if user cancelled
    const errorMsg = error.message || error.error || '';
    if (
      errorMsg !== 'User canceled' && 
      errorMsg !== 'The operation was cancelled.' &&
      errorMsg !== 'The user canceled the authorization attempt.'
    ) {
      toast.error('Apple authentication failed. Please try again.');
    }
    if (onError) {
      onError();
    }
  };

  const handleAppleLogin = async () => {
    if (isAuthenticatingRef.current) return;
    isAuthenticatingRef.current = true;
    try {
      const clientId = (typeof window !== 'undefined' && window.__ENV && window.__ENV.REACT_APP_APPLE_CLIENT_ID) || process.env.REACT_APP_APPLE_CLIENT_ID;
      const redirectURI = (typeof window !== 'undefined' && window.__ENV && window.__ENV.REACT_APP_APPLE_REDIRECT_URI) || process.env.REACT_APP_APPLE_REDIRECT_URI;

      if (!clientId || !redirectURI) {
        console.error('Apple Sign-In Configuration Missing: REACT_APP_APPLE_CLIENT_ID or REACT_APP_APPLE_REDIRECT_URI');
        toast.error('Apple Sign-In is not configured yet. Please contact support.');
        return;
      }

      // Capawesome requires initialization for Android and Web
      if (platform !== 'ios') {
        await AppleSignIn.initialize({
          clientId: clientId,
        });
      }

      const result = await AppleSignIn.signIn({
        scopes: [SignInScope.Email, SignInScope.FullName],
        redirectUrl: redirectURI,
      });
      
      console.log('[Apple Sign-In] Result:', JSON.stringify(result, null, 2));

      if (result?.idToken) {
        // Extract full name if available (Apple only sends it on the very first login)
        let fullName = null;
        if (result.givenName || result.familyName) {
            fullName = `${result.givenName || ''} ${result.familyName || ''}`.trim();
        }
        
        handleSuccess(result.idToken, fullName);
      } else {
        handleError(new Error('No ID Token returned from Apple.'));
      }
    } catch (error) {
      handleError(error);
    }
  };

  const labelMap = {
    signin_with: 'Sign in with Apple',
    signup_with: 'Sign up with Apple',
    continue_with: 'Continue with Apple',
  };
  const label = labelMap[text] || 'Sign in with Apple';

  return (
    <div className="w-full">
      <button
        id="apple-signin-button"
        type="button"
        onClick={handleAppleLogin}
        className="flex items-center justify-center gap-3 px-4 w-full bg-white hover:bg-gray-50 transition-colors duration-200 text-[#1f1f1f] text-[14px] font-medium border border-[#dadce0] shadow-sm rounded-xl"
        style={{ 
          height: '40px'
        }}
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15.548 10.963c-.023 2.15 1.765 3.181 1.789 3.193-.016.052-.28 1.054-1.018 2.126-.642.92-1.304 1.833-2.316 1.852-1.012.019-1.336-.593-2.492-.593-1.157 0-1.517.574-2.474.612-1.012.037-1.74-.976-2.385-1.908-1.319-1.907-2.329-5.385-.968-7.742.664-1.168 1.867-1.91 3.178-1.928 1.002-.019 1.942.668 2.553.668.611 0 1.748-.813 2.946-.69 1.488.062 1.944.595 2.454.73-.024.129-1.246.732-1.267 3.68zM12.91 3.496C13.447 2.848 13.81 1.948 13.71 1.05c-.772.031-1.706.514-2.259 1.162-.497.575-.93 1.493-.812 2.373.86.066 1.724-.439 2.271-1.089z" fill="black"/>
        </svg>
        <span>{label}</span>
      </button>
    </div>
  );
};

export default AppleAuthButton;

